/**
 * useModuleMoneyAward - Hook for awarding money from module questions with idempotency
 * 
 * Uses the award-module-money Edge Function which:
 * - Runs with service_role privileges (allows non-auth students)
 * - Calls award_module_money_v1 RPC internally
 * - Prevents duplicate awards for the same (player_id, question_id)
 * - Updates player_financial_status atomically
 * - Returns the new total_earned amount
 */
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePlayerAuth } from '@/hooks/usePlayerAuth';
import { useCurrentPlayer } from '@/hooks/useCurrentPlayer';
import { log, logError } from '@/lib/log';
import { updateCachedFinancialStatus } from '@/stores/financialStatusStore';
import { invalidateCacheByRpc } from '@/services/rpcClient';

interface AwardResult {
  success: boolean;
  awarded: boolean;
  eurosDelta: number;
  newTotalEarned: number;
  reason?: string;
}

export const useModuleMoneyAward = () => {
  const { playerSession } = usePlayerAuth();
  const { currentPlayer } = useCurrentPlayer();

  const awardQuestionMoney = useCallback(async (
    questionId: string,
    sectionId: string,
    moduleId: string,
    eurosDelta: number
  ): Promise<AwardResult> => {
    const traceId = crypto.randomUUID();

    if (!playerSession?.sessionToken || !currentPlayer?.id) {
      logError('[module-answer] No player session for money award');
      return { success: false, awarded: false, eurosDelta: 0, newTotalEarned: 0 };
    }

    if (eurosDelta <= 0) {
      log('[module-answer] No money to award (eurosDelta <= 0)');
      return { success: true, awarded: false, eurosDelta: 0, newTotalEarned: 0 };
    }

    try {
      log('[money] Calling award-module-money Edge Function', {
        player_id: currentPlayer.id.slice(-8),
        question_id: questionId.slice(-8),
        euros_delta: eurosDelta,
        module_key: moduleId,
        trace_id: traceId.slice(-8),
      });

      // Call Edge Function instead of direct RPC
      const { data, error } = await supabase.functions.invoke('award-module-money', {
        body: {
          session_token: playerSession.sessionToken,
          player_id: currentPlayer.id,
          question_id: questionId,
          euros_delta: eurosDelta,
          trace_id: traceId,
          module_key: moduleId,
          section_key: sectionId,
        },
      });

      if (error) {
        logError('[money] Edge Function error', error);
        return { success: false, awarded: false, eurosDelta: 0, newTotalEarned: 0 };
      }

      // Parse Edge Function response
      const result = data as {
        success: boolean;
        awarded: boolean;
        eurosDelta: number;
        newTotalEarned: number;
        currentMoney: number;
        reason?: string;
        traceId?: string;
      };

      if (!result || result.success !== true) {
        logError('[money] Edge Function NOT OK', result);
        return { success: false, awarded: false, eurosDelta: 0, newTotalEarned: 0 };
      }

      log('[money] award result traceId=' + (result.traceId || traceId).slice(-8) + 
          ' awarded=' + result.awarded + 
          ' totalEarned=' + result.newTotalEarned + 
          (result.reason ? ' reason=' + result.reason : ''));

      // Update cache immediately with new financial values
      if (result.awarded) {
        // Invalidate RPC cache for financial status and balance
        invalidateCacheByRpc('get_freeworld_balance');
        invalidateCacheByRpc('get_or_create_financial_status_with_session');
        
        updateCachedFinancialStatus({
          total_earned: result.newTotalEarned,
          current_money: result.currentMoney,
        });
        
        // Emit event so dashboard can refresh if needed
        window.dispatchEvent(new CustomEvent('moneyChanged', {
          detail: {
            newTotalEarned: result.newTotalEarned,
            currentMoney: result.currentMoney,
            eurosDelta: result.eurosDelta,
            source: 'module_question',
            traceId: result.traceId || traceId,
          },
        }));
        
        // Emit moneyAwarded event for useFreeWorldBalance to refresh
        window.dispatchEvent(new CustomEvent('moneyAwarded', {
          detail: {
            eurosDelta: result.eurosDelta,
            newBalance: result.currentMoney,
            source: 'module_question',
          },
        }));
      }

      return {
        success: result.success,
        awarded: result.awarded,
        eurosDelta: result.awarded ? result.eurosDelta : 0,
        newTotalEarned: result.newTotalEarned,
        reason: result.reason,
      };
    } catch (err) {
      logError('[module-answer] Exception awarding money (traceId=' + traceId + '):', err);
      return { success: false, awarded: false, eurosDelta: 0, newTotalEarned: 0 };
    }
  }, [playerSession?.sessionToken, currentPlayer?.id]);

  return { awardQuestionMoney };
};
